import type { RoleCapability, RolePreset } from "./app/types";

function normalizeWhitespace(value: string | null | undefined) {
  return value?.trim() ?? "";
}

type WorkspaceRoleDefinition = {
  label: string;
  detail: string;
  capabilities: RoleCapability[];
};

type RoleCapabilityDefinition = {
  capability: RoleCapability;
  label: string;
  detail: string;
  editable: boolean;
};

export const workspaceRoleCapabilityDefinitions: RoleCapabilityDefinition[] = [
  {
    capability: "tickets:view",
    label: "Tickets : View",
    detail: "Open ticket lists and review ticket details without making changes.",
    editable: true,
  },
  {
    capability: "tickets:edit",
    label: "Tickets : Edit",
    detail: "Create, update, assign, and resolve tickets.",
    editable: true,
  },
  {
    capability: "tickets:delete",
    label: "Tickets : Delete",
    detail: "Permanently delete tickets from the workspace.",
    editable: true,
  },
  {
    capability: "customer:view",
    label: "Customers : View",
    detail: "Open customer accounts and review account details without making changes.",
    editable: true,
  },
  {
    capability: "customers:edit",
    label: "Customers : Edit",
    detail: "Update customer accounts, contacts, products, and lifecycle notes.",
    editable: true,
  },
  {
    capability: "customers:delete",
    label: "Customers : Delete",
    detail: "Permanently delete customer accounts and their related tickets.",
    editable: true,
  },
  {
    capability: "kb:view",
    label: "Knowledge : View",
    detail: "Open knowledge base collections and read published workspace articles.",
    editable: true,
  },
  {
    capability: "kb:create",
    label: "Knowledge : Create",
    detail: "Draft and publish knowledge base articles.",
    editable: true,
  },
  {
    capability: "kb:manage",
    label: "Knowledge : Manage",
    detail: "Create collections and manage published knowledge content.",
    editable: true,
  },
  {
    capability: "users:view",
    label: "Users : View",
    detail: "Open the users directory and review role assignments.",
    editable: true,
  },
  {
    capability: "users:manage",
    label: "Users : Manage",
    detail: "Invite, update, and remove Users.",
    editable: false,
  },
  {
    capability: "notifications:view",
    label: "Notifications : View",
    detail: "Open service notices and retrieve platform announcements for this workspace.",
    editable: true,
  },
  {
    capability: "archive:view",
    label: "Archive : View",
    detail: "Open the archive view and inspect archived workspace data.",
    editable: true,
  },
  {
    capability: "archive:edit",
    label: "Archive : Edit",
    detail: "Delete archived records and run archive maintenance actions.",
    editable: true,
  },
  {
    capability: "settings:view",
    label: "Settings : View",
    detail: "Open the settings workspace and review workspace configuration.",
    editable: true,
  },
  {
    capability: "settings:manage",
    label: "Settings : Manage",
    detail: "Update branding, ticket categories, and administrative workspace settings.",
    editable: true,
  },
  {
    capability: "calendar:view",
    label: "Calendar : View",
    detail: "Open the calendar and review scheduled items without making changes.",
    editable: true,
  },
  {
    capability: "calendar:create",
    label: "Calendar : Create",
    detail: "Create, update, and delete scheduled calendar items without managing assignment types.",
    editable: true,
  },
  {
    capability: "calendar:manage",
    label: "Calendar : Manage",
    detail: "Create, edit, and delete assignment types from the user calendar.",
    editable: true,
  },
  {
    capability: "api:access",
    label: "API : Access",
    detail: "Generate personal API keys and access workspace API endpoints programmatically.",
    editable: true,
  },
  {
    capability: "billing:manage",
    label: "Billing : Manage",
    detail: "Update workspace commercial defaults and billing configuration.",
    editable: true,
  },
  {
    capability: "items:view",
    label: "Items : View",
    detail: "Open the items catalog and review pricing, SLA, and bundle relationships.",
    editable: true,
  },
  {
    capability: "items:manage",
    label: "Items : Manage",
    detail: "Create, edit, and remove product plans and related items.",
    editable: true,
  },
  {
    capability: "expenses:view",
    label: "Expenses : View",
    detail: "Open the expenses ledger and review expense records without making changes.",
    editable: true,
  },
  {
    capability: "expenses:edit",
    label: "Expenses : Edit",
    detail: "Create and update expense records.",
    editable: true,
  },
  {
    capability: "expenses:delete",
    label: "Expenses : Delete",
    detail: "Permanently delete expense records from the workspace.",
    editable: true,
  },
  {
    capability: "expenses:view-all",
    label: "Expenses : View all",
    detail: "See every member's expenses, not just your own.",
    editable: true,
  },
  {
    capability: "expenses:edit-all",
    label: "Expenses : Edit all",
    detail: "Edit every member's expenses, not just your own.",
    editable: true,
  },
  {
    capability: "vendors:view",
    label: "Vendors : View",
    detail: "Open the vendor directory and review vendor details without making changes.",
    editable: true,
  },
  {
    capability: "vendors:edit",
    label: "Vendors : Edit",
    detail: "Create and update vendor records.",
    editable: true,
  },
  {
    capability: "vendors:delete",
    label: "Vendors : Delete",
    detail: "Permanently delete vendors and their related expenses.",
    editable: true,
  },
  {
    capability: "reporting:view",
    label: "Reporting : View",
    detail: "Access dashboards and reporting views.",
    editable: true,
  },
  {
    capability: "reporting:manage",
    label: "Reporting : Manage",
    detail: "Build and manage report packs and exports.",
    editable: true,
  },
];

const workspaceRoleCapabilityDefinitionByKey = new Map(
  workspaceRoleCapabilityDefinitions.map(definition => [definition.capability, definition]),
);

const workspaceRoleDefinitions: Record<string, WorkspaceRoleDefinition> = {
  owner: {
    label: "Owner",
    detail: "Full access to workspace settings, team permissions, branding, integrations, billing, and operational data.",
    capabilities: ["tickets:view", "tickets:edit", "tickets:delete", "customer:view", "customers:edit", "customers:delete", "kb:view", "kb:create", "kb:manage", "users:view", "users:manage", "notifications:view", "archive:view", "archive:edit", "settings:view", "settings:manage", "calendar:view", "calendar:create", "calendar:manage", "api:access", "billing:manage", "items:view", "items:manage", "expenses:view", "expenses:edit", "expenses:delete", "expenses:view-all", "expenses:edit-all", "vendors:view", "vendors:edit", "vendors:delete", "reporting:view", "reporting:manage"],
  },
  admin: {
    label: "Admin",
    detail: "Runs the workspace day to day, including users, categories, ticket operations, CRM updates, and knowledge management.",
    capabilities: ["tickets:view", "tickets:edit", "tickets:delete", "customer:view", "customers:edit", "customers:delete", "kb:view", "kb:create", "kb:manage", "users:view", "users:manage", "notifications:view", "archive:view", "archive:edit", "settings:view", "settings:manage", "calendar:view", "calendar:create", "calendar:manage", "api:access", "billing:manage", "items:view", "items:manage", "expenses:view", "expenses:edit", "expenses:delete", "expenses:view-all", "expenses:edit-all", "vendors:view", "vendors:edit", "vendors:delete", "reporting:view", "reporting:manage"],
  },
  agent: {
    label: "Agent",
    detail: "Works tickets, reads customer context, and creates knowledge articles inside the categories assigned by an admin.",
    capabilities: ["tickets:view", "tickets:edit", "customer:view", "kb:view", "kb:create", "calendar:view", "calendar:create"],
  },
  "customer-manager": {
    label: "Customer manager",
    detail: "Updates customer accounts, contacts, products, and notes without changing ticket workflows.",
    capabilities: ["customer:view", "customers:edit", "items:view"],
  },
  "knowledge-manager": {
    label: "Knowledge manager",
    detail: "Maintains collections and published content for the knowledge base without managing users or customer records.",
    capabilities: ["kb:view", "kb:create", "kb:manage"],
  },
  "reporting-manager": {
    label: "Reporting manager",
    detail: "Builds, runs, exports, and schedules reports without managing users, tickets, or customer records.",
    capabilities: ["reporting:view", "reporting:manage"],
  },
  viewer: {
    label: "Viewer",
    detail: "Read-only access across tickets, customers, and knowledge content, with visibility into reporting dashboards.",
    capabilities: ["tickets:view", "customer:view", "kb:view", "calendar:view", "items:view", "reporting:view"],
  },
};

const roleCapabilities: Record<string, RoleCapability[]> = Object.fromEntries(
  Object.entries(workspaceRoleDefinitions).map(([key, definition]) => [key, definition.capabilities]),
);

export function getEditableWorkspaceRoleCapabilityDefinitions() {
  return workspaceRoleCapabilityDefinitions.filter(definition => definition.editable);
}

export function getRoleCapabilityLabel(capability: RoleCapability) {
  return workspaceRoleCapabilityDefinitionByKey.get(capability)?.label ?? capability;
}

export function getRoleCapabilityDetail(capability: RoleCapability) {
  return workspaceRoleCapabilityDefinitionByKey.get(capability)?.detail ?? "";
}

export function normalizeRoleCapabilities(value: readonly string[] | null | undefined): RoleCapability[] {
  if (!value) {
    return [];
  }

  const allowedCapabilities = new Set<RoleCapability>(workspaceRoleCapabilityDefinitions.map(definition => definition.capability));
  return Array.from(new Set(value.filter((capability): capability is RoleCapability => allowedCapabilities.has(capability as RoleCapability))));
}

export function isSystemWorkspaceRole(value: string | null | undefined) {
  const canonicalRole = getCanonicalWorkspaceRoleKey(value);
  return canonicalRole in workspaceRoleDefinitions;
}

export function getWorkspaceRoleDefaultCapabilities(value: string | null | undefined) {
  const canonicalRole = getCanonicalWorkspaceRoleKey(value);
  return [...(roleCapabilities[canonicalRole] ?? [])];
}

export function getRoleCapabilities(value: string | null | undefined, rolePresets?: RolePreset[]) {
  const canonicalRole = getCanonicalWorkspaceRoleKey(value);

  // System workspace roles are immutable, so their default capability set is always
  // authoritative. This prevents stale role_presets rows (seeded before a capability
  // such as expenses:view-all existed) from stripping default capabilities.
  if (isSystemWorkspaceRole(canonicalRole)) {
    return getWorkspaceRoleDefaultCapabilities(value);
  }

  const matchingPreset = rolePresets?.find(preset => getCanonicalWorkspaceRoleKey(preset.role) === canonicalRole);

  if (matchingPreset) {
    return normalizeRoleCapabilities(matchingPreset.capabilities);
  }

  return getWorkspaceRoleDefaultCapabilities(value);
}

export function normalizeRoleKey(value: string | null | undefined) {
  return normalizeWhitespace(value).toLowerCase();
}

export function getCanonicalWorkspaceRoleKey(value: string | null | undefined) {
  const normalizedRole = normalizeRoleKey(value);

  switch (normalizedRole) {
    case "owner":
      return "owner";
    case "admin":
      return "admin";
    case "agent":
      return "agent";
    case "customer manager":
      return "customer-manager";
    case "knowledge manager":
      return "knowledge-manager";
    case "reporting manager":
      return "reporting-manager";
    case "viewer":
      return "viewer";
    default:
      return normalizedRole;
  }
}

export function getWorkspaceRoleLabel(value: string | null | undefined) {
  const normalizedRole = normalizeWhitespace(value);

  if (!normalizedRole) {
    return "Team member";
  }

  const canonicalRole = getCanonicalWorkspaceRoleKey(normalizedRole);
  const knownLabel = workspaceRoleDefinitions[canonicalRole]?.label;

  if (knownLabel) {
    return knownLabel;
  }

  return normalizedRole
    .replace(/[-_]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function getWorkspaceRoleDefaultDetail(value: string | null | undefined) {
  const canonicalRole = getCanonicalWorkspaceRoleKey(value);
  return workspaceRoleDefinitions[canonicalRole]?.detail ?? "No role preset description has been defined for this role yet.";
}

function hasRoleCapability(value: string | null | undefined, capability: RoleCapability) {
  const normalizedRole = getCanonicalWorkspaceRoleKey(value);
  return roleCapabilities[normalizedRole]?.includes(capability) ?? false;
}

function hasResolvedRoleCapability(value: string | null | undefined, capability: RoleCapability, rolePresets?: RolePreset[]) {
  return getRoleCapabilities(value, rolePresets).includes(capability);
}

export function isOwnerRole(value: string | null | undefined) {
  return getCanonicalWorkspaceRoleKey(value) === "owner";
}

export function isAgentRole(value: string | null | undefined) {
  return getCanonicalWorkspaceRoleKey(value) === "agent";
}

export function canManageWorkspaceAccess(value: string | null | undefined) {
  const role = getCanonicalWorkspaceRoleKey(value);
  return role === "owner" || role === "admin";
}

export function canEditTickets(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "tickets:edit", rolePresets);
}

export function canViewTickets(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "tickets:view", rolePresets) || canEditTickets(value, rolePresets) || canDeleteTickets(value, rolePresets);
}

export function canDeleteTickets(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "tickets:delete", rolePresets);
}

export function canViewCustomers(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "customer:view", rolePresets) || canEditCustomers(value, rolePresets) || canDeleteCustomers(value, rolePresets);
}

export function canEditCustomers(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "customers:edit", rolePresets);
}

export function canDeleteCustomers(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "customers:delete", rolePresets);
}

export function canCreateKnowledgeArticles(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "kb:create", rolePresets);
}

export function canViewKnowledgeBase(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "kb:view", rolePresets) || canCreateKnowledgeArticles(value, rolePresets) || canManageKnowledgeBase(value, rolePresets);
}

export function canManageKnowledgeBase(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "kb:manage", rolePresets);
}

export function canViewUsers(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "users:view", rolePresets) || canEditUsers(value, rolePresets) || canManageWorkspaceAccess(value);
}

export function canEditUsers(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "users:manage", rolePresets) || canManageWorkspaceAccess(value);
}

export function canViewNotifications(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "notifications:view", rolePresets);
}

export function canViewArchive(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "archive:view", rolePresets) || canEditArchive(value, rolePresets);
}

export function canEditArchive(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "archive:edit", rolePresets);
}

export function canViewSettings(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "settings:view", rolePresets) || canManageAdminSettings(value, rolePresets);
}

export function canManageAdminSettings(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "settings:manage", rolePresets);
}

export function canManageCalendarAssignmentTypes(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "calendar:manage", rolePresets);
}

export function canCreateCalendarAssignments(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "calendar:create", rolePresets) || canManageCalendarAssignmentTypes(value, rolePresets);
}

export function canViewCalendar(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "calendar:view", rolePresets) || canCreateCalendarAssignments(value, rolePresets);
}

export function canAccessApi(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "api:access", rolePresets);
}

export function canManageBilling(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "billing:manage", rolePresets);
}

export function canViewItems(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "items:view", rolePresets) || canManageItems(value, rolePresets);
}

export function canManageItems(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "items:manage", rolePresets);
}

export function canViewExpenses(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "expenses:view", rolePresets) || canEditExpenses(value, rolePresets) || canDeleteExpenses(value, rolePresets) || canViewAllExpenses(value, rolePresets);
}

export function canViewAllExpenses(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "expenses:view-all", rolePresets) || canEditAllExpenses(value, rolePresets);
}

export function canEditExpenses(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "expenses:edit", rolePresets);
}

export function canEditAllExpenses(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "expenses:edit-all", rolePresets);
}

export function canDeleteExpenses(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "expenses:delete", rolePresets);
}

export function canViewVendors(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "vendors:view", rolePresets) || canEditVendors(value, rolePresets) || canDeleteVendors(value, rolePresets);
}

export function canEditVendors(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "vendors:edit", rolePresets);
}

export function canDeleteVendors(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "vendors:delete", rolePresets);
}

export function canViewReporting(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "reporting:view", rolePresets) || hasResolvedRoleCapability(value, "reporting:manage", rolePresets);
}

export function canManageReporting(value: string | null | undefined, rolePresets?: RolePreset[]) {
  return hasResolvedRoleCapability(value, "reporting:manage", rolePresets);
}